import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useInfiniteHits, useInstantSearch } from "react-instantsearch";

import NoResultsMessage from "../../algolia_search/no_results_message";
import useInfiniteScroll from "../../hooks/use_infinite_scroll";

import EmptyResults from "./EmptyResults";
import MediaProfileCardGrid from "../card/MediaProfileCardGrid";
import MediaProfileCardList from "../card/MediaProfileCardList";
import mediaListActionColumnWidth from "../card/listActionColumn";
import { viewerIsMediaProfile } from "../card/messageGating";
import { listHeaderRowStyle } from "../../source_directory/utils";

function MediaCardSkeletonRow({ MediaProfileCardSkeleton }) {
  return (
    <>
      <div style={{ gridRow: "span 4" }}>
        <MediaProfileCardSkeleton />
      </div>
      <div className="d-none d-md-block" style={{ gridRow: "span 4" }}>
        <MediaProfileCardSkeleton />
      </div>
      <div className="d-none d-xl-block" style={{ gridRow: "span 4" }}>
        <MediaProfileCardSkeleton />
      </div>
    </>
  );
}

MediaCardSkeletonRow.propTypes = {
  MediaProfileCardSkeleton: PropTypes.elementType.isRequired,
};

function InfiniteHits({ MediaProfileCardSkeleton, viewMode = "grid" }) {
  const { status } = useInstantSearch();
  const isLoading = status === "stalled";

  const { hits, isLastPage, showMore } = useInfiniteHits();
  const hasMore = !isLastPage;

  const [lastTriggeredAt, scrollTriggerRef] = useInfiniteScroll({ hasMore });

  useEffect(() => {
    if (lastTriggeredAt > 0) showMore();
  }, [lastTriggeredAt, showMore]);

  if (viewMode === "list") {
    // Reserve the trailing per-row action column (see MediaProfileCardList) so the column headers
    // line up with the row content.
    const actionColumnWidth = mediaListActionColumnWidth(viewerIsMediaProfile());

    return (
      <>
        <NoResultsMessage customMessage={<EmptyResults />} hits={hits} />
        {hits.length > 0 && (
          <div style={{ ...listHeaderRowStyle, padding: "0 16px 8px" }}>
            <span style={{ flex: "0 0 200px" }}>Name</span>
            <span style={{ flex: "0 0 130px" }}>Location</span>
            <span style={{ flex: 1 }}>Writes for</span>
            <span aria-hidden="true" style={{ flex: `0 0 ${actionColumnWidth}px` }} />
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {hits.map((hit) => (
            <MediaProfileCardList key={hit.objectID} hit={hit} />
          ))}
        </div>
        {hasMore && <div ref={scrollTriggerRef} />}
      </>
    );
  }

  return (
    <>
      <NoResultsMessage customMessage={<EmptyResults />} hits={hits} />
      <div className="sd-cards-grid">
        {isLoading && <MediaCardSkeletonRow MediaProfileCardSkeleton={MediaProfileCardSkeleton} />}
        {!isLoading && hits.map((hit) => <MediaProfileCardGrid key={hit.objectID} hit={hit} />)}
      </div>
      {hasMore && (
        <div ref={scrollTriggerRef} className="sd-cards-grid sd-cards-grid--loading-more">
          <MediaCardSkeletonRow MediaProfileCardSkeleton={MediaProfileCardSkeleton} />
        </div>
      )}
    </>
  );
}

InfiniteHits.propTypes = {
  MediaProfileCardSkeleton: PropTypes.elementType,
  viewMode: PropTypes.oneOf(["grid", "list"]),
};

export default InfiniteHits;
